import { SwapOutlined } from "@ant-design/icons";
import { useCan } from "@refinedev/core";
import {
  Alert,
  Button,
  Flex,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ResourceEnum } from "accessControlProvider";
import { ShowSection } from "components/show";
import type { IUkhoNoticeRow } from "interfaces";
import React, { useState } from "react";
import {
  apiErrorMessage,
  useUnmatchNotice,
  useVesselNotices,
} from "../../vessel-transfer/ukho/api";
import { OriginalEmailModal } from "../../vessel-transfer/ukho/OriginalEmailModal";
import { ParkWithDateModal } from "../../vessel-transfer/ukho/ParkWithDateModal";
import { ReactivateWithDateModal } from "../../vessel-transfer/ukho/ReactivateWithDateModal";
import {
  SecondaryLine,
  WrappingTag,
} from "../../vessel-transfer/ukho/workTableColumns";
import {
  formatDay,
  isUkhoNotice,
  matchedByText,
  noticesInYearText,
  shortServices,
  ukhoDialogTarget,
  ukhoStatus,
  UkhoStatus,
  ukhoTimeline,
  UkhoTimelineEntry,
  ukTimeText,
  whatText,
} from "../lib/ukho-status";

const FOOTNOTE =
  "Days are UKHO's. A park or reactivation done in eMariner is listed too, so the two can be compared.";

const ACTION_BUTTON_TEXT: Record<"park" | "reactivate", string> = {
  park: "Park with this date",
  reactivate: "Reactivate with this date",
};

/** Design section 6: UKHO's "arrived" green, "left" red; eMariner's rows grey. */
const WHAT_TAG_COLORS: Record<UkhoTimelineEntry["kind"], string | undefined> = {
  in: "green",
  out: "red",
  park: undefined,
  reactivation: undefined,
};

export interface UkhoTransfersCardProps {
  vesselId: number;
  /** The day the status line counts from; the vessel page leaves it to now. */
  today?: Date;
}

/**
 * Design 11.1, the vessel page's "UKHO transfers" block. It renders nothing,
 * and sends no request, for a role the notice API refuses: the web ACL
 * mirrors the API's three roles on the ukho-transfer-notice resource.
 */
export const UkhoTransfersCard: React.FC<UkhoTransfersCardProps> = (props) => {
  const { data: noticeAccess } = useCan({
    resource: ResourceEnum.UkhoTransferNotice,
    action: "list",
  });
  return noticeAccess?.can ? <UkhoTransfersSection {...props} /> : null;
};

const UkhoTransfersSection: React.FC<UkhoTransfersCardProps> = ({
  vesselId,
  today = new Date(),
}) => {
  const {
    data: view,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useVesselNotices(vesselId);
  const { mutate: unmatchNotice, mutation: unmatchMutation } =
    useUnmatchNotice();
  const [openDialog, setOpenDialog] = useState<UkhoStatus["action"]>(null);
  const [emailNoticeId, setEmailNoticeId] = useState<number | null>(null);

  const status = view ? ukhoStatus(view, today) : null;
  const dialogTarget = view && status ? ukhoDialogTarget(view, status) : null;
  const timeline = view ? ukhoTimeline(view) : [];
  // The table hides only when it would have no row at all (ruling
  // 2026-10-30, fix round 1): a ship with no notice and no park or
  // reactivation done in eMariner shows the grey status line alone, while a
  // park row without a notice keeps the table under the grey line. While the
  // view loads, the table stays to carry the spinner.
  const showTimeline = !view || timeline.length > 0;

  const handleTransferDone = () => {
    setOpenDialog(null);
    refetch();
  };

  const sendNoticeBack = (notice: IUkhoNoticeRow) => {
    unmatchNotice(
      { id: notice.id },
      {
        onSuccess: () => {
          message.success(
            `Notice ${notice.transferId} is back in Unmatched notices`
          );
          refetch();
        },
        onError: (unmatchError) => {
          message.error(apiErrorMessage(unmatchError));
        },
      }
    );
  };

  const columns = buildTimelineColumns({
    onShowEmail: setEmailNoticeId,
    onSendBack: sendNoticeBack,
  });

  return (
    <ShowSection
      title="UKHO transfers"
      subtitle={
        view ? noticesInYearText(view.notices, today.getFullYear()) : undefined
      }
      icon={<SwapOutlined />}
      variant="default"
    >
      {isError ? (
        <Alert type="error" showIcon message={apiErrorMessage(error)} />
      ) : (
        <>
          {status && (
            <UkhoStatusLine
              status={status}
              withTableBelow={showTimeline}
              onAction={() => setOpenDialog(status.action)}
            />
          )}
          {showTimeline && (
            <>
              <Table<UkhoTimelineEntry>
                rowKey={timelineRowKey}
                columns={columns}
                dataSource={timeline}
                loading={isLoading || isFetching || unmatchMutation.isPending}
                pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
              />
              <Typography.Paragraph
                type="secondary"
                style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
              >
                {FOOTNOTE}
              </Typography.Paragraph>
            </>
          )}
        </>
      )}
      {dialogTarget && openDialog === "park" && (
        <ParkWithDateModal
          open
          target={dialogTarget}
          onClose={() => setOpenDialog(null)}
          onDone={handleTransferDone}
        />
      )}
      {dialogTarget && openDialog === "reactivate" && (
        <ReactivateWithDateModal
          open
          target={dialogTarget}
          onClose={() => setOpenDialog(null)}
          onDone={handleTransferDone}
        />
      )}
      <OriginalEmailModal
        noticeId={emailNoticeId}
        onClose={() => setEmailNoticeId(null)}
      />
    </ShowSection>
  );
};

/**
 * The status line: red with a button when eMariner lags UKHO, green, or grey.
 * Alone in the block (no table below) it keeps no gap under it.
 */
const UkhoStatusLine: React.FC<{
  status: UkhoStatus;
  withTableBelow: boolean;
  onAction: () => void;
}> = ({ status, withTableBelow, onAction }) => {
  const { token } = theme.useToken();
  const tones = {
    red: {
      background: token.colorErrorBg,
      border: token.colorErrorBorder,
      text: token.colorErrorText,
    },
    green: {
      background: token.colorSuccessBg,
      border: token.colorSuccessBorder,
      text: token.colorSuccessText,
    },
    grey: {
      background: token.colorFillAlter,
      border: token.colorBorder,
      text: token.colorText,
    },
  };
  const tone = status.tone === "none" ? tones.grey : tones[status.tone];

  return (
    <Flex
      data-tone={status.tone}
      align="center"
      gap={12}
      wrap
      style={{
        padding: "10px 14px",
        marginBottom: withTableBelow ? 16 : 0,
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${tone.border}`,
        background: tone.background,
      }}
    >
      <Typography.Text style={{ flex: "1 1 320px", color: tone.text }}>
        {status.title ? (
          <>
            <strong>{`${status.title}.`}</strong>{" "}
          </>
        ) : null}
        {status.text}
      </Typography.Text>
      {status.action && (
        <Button type="primary" size="small" onClick={onAction}>
          {ACTION_BUTTON_TEXT[status.action]}
        </Button>
      )}
    </Flex>
  );
};

const SecondaryText: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <Typography.Text type="secondary">{children}</Typography.Text>;

const timelineRowKey = (entry: UkhoTimelineEntry): string =>
  isUkhoNotice(entry)
    ? `notice-${entry.id}`
    : `${entry.kind}-${entry.vesselTransferId}`;

/** "requested by <user>" under an arrival, "by <user>" under an eMariner row. */
const whoLine = (entry: UkhoTimelineEntry): React.ReactNode => {
  if (!isUkhoNotice(entry)) {
    return entry.performedBy ? (
      <SecondaryLine>{`by ${entry.performedBy}`}</SecondaryLine>
    ) : null;
  }
  return entry.kind === "in" && entry.requestedBy ? (
    <SecondaryLine>{`requested by ${entry.requestedBy}`}</SecondaryLine>
  ) : null;
};

interface TimelineColumnHandlers {
  onShowEmail: (noticeId: number) => void;
  onSendBack: (notice: IUkhoNoticeRow) => void;
}

/**
 * Design 11.1's table. House table pattern: every column has a fixed width
 * and the long ones wrap (`wrap-text`), so the table never scrolls sideways.
 */
const buildTimelineColumns = ({
  onShowEmail,
  onSendBack,
}: TimelineColumnHandlers): ColumnsType<UkhoTimelineEntry> => [
  {
    title: "Day",
    key: "day",
    width: 130,
    render: (_, entry) =>
      isUkhoNotice(entry) ? (
        <>
          <div>{formatDay(entry.noticeDate)}</div>
          {entry.kind === "in" && entry.transferredAt && (
            <SecondaryLine>{ukTimeText(entry.transferredAt)}</SecondaryLine>
          )}
        </>
      ) : (
        formatDay(entry.day)
      ),
  },
  {
    title: "What",
    key: "what",
    width: 200,
    className: "wrap-text",
    render: (_, entry) => (
      <>
        <WrappingTag color={WHAT_TAG_COLORS[entry.kind]}>
          {whatText(entry)}
        </WrappingTag>
        {whoLine(entry)}
      </>
    ),
  },
  {
    title: "Services",
    key: "services",
    width: 150,
    className: "wrap-text",
    render: (_, entry) =>
      isUkhoNotice(entry) ? (
        shortServices(entry.services)
      ) : (
        <SecondaryText>all</SecondaryText>
      ),
  },
  {
    title: "UKHO transfer",
    key: "transferId",
    width: 120,
    render: (_, entry) =>
      isUkhoNotice(entry) ? (
        entry.transferId
      ) : (
        <SecondaryText>park record</SecondaryText>
      ),
  },
  {
    title: "Matched by",
    key: "matchedBy",
    width: 130,
    render: (_, entry) =>
      isUkhoNotice(entry) ? (
        matchedByText(entry.matchBasis)
      ) : (
        <SecondaryText>n/a</SecondaryText>
      ),
  },
  {
    title: "",
    key: "actions",
    width: 200,
    className: "wrap-text",
    render: (_, entry) =>
      isUkhoNotice(entry) ? (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            onClick={() => onShowEmail(entry.id)}
          >
            original email
          </Button>
          {entry.vesselId !== null && (
            <Popconfirm
              title="Send this notice back to Unmatched notices?"
              okText="Send back"
              cancelText="Cancel"
              onConfirm={() => onSendBack(entry)}
            >
              <Button type="link" size="small">
                Not this ship
              </Button>
            </Popconfirm>
          )}
        </Space>
      ) : null,
  },
];
